import React, { useCallback, useState, useEffect } from 'react';
import { Form, Header, SpaceBetween, ContentLayout, Button, AppLayout, Flashbar } from '@amzn/awsui-components-core';
import { useAuth } from '../../context/AuthContextProvider';
import { useEventsApiContext } from '../../context/EventsApiContextProvider';
import { DFPHomePageSideNavigation } from '../../dfp-home-page/DFPHomePageSideNavigation';
import './EAHuddleHomePage.scss';

import { useMeetingForm } from './hooks/useMeetingForm';
import { useAttendeeManagement } from './hooks/useAttendeeManagement';
import { useDocumentProcessing } from './hooks/useDocumentProcessing';
import { useSessionPersistence } from './hooks/useSessionPersistence';
import { useOutlookIntegration } from './hooks/useOutlookIntegration';
import { useFlashMessages } from './hooks/useFlashMessages';

import { AddAttendeesContainer } from './containers/AddAttendeesContainer';
import { InfoPanel } from './components/InfoPanel';

import { Employee } from './interfaces/interface';
import { generateMultiBadgerUrl } from '../shared/urlUtils';

import { exportAttendeeHierarchyToCsv } from './utils/csvExportUtils';
import { logger } from 'src/logger';

export const EAHuddleHomePage: React.FC = () => {
  const { Alias } = useAuth();
  const eventsApiContext = useEventsApiContext();

  const [showUploadTemplateModal, setShowUploadTemplateModal] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(true);
  const [navigationOpen, setNavigationOpen] = useState(false);

  useEffect(() => {
    logger.info('User authenticated successfully!', { app: 'EA Huddle' });
  }, []);

  // Flash messages for user notifications
  const { flashMessages, addFlashMessage, removeFlashMessage } = useFlashMessages();

  const { meetingFormData, setMeetingFormData, handleInputChange, manuallyEditedMeetingFormDataFields } = useMeetingForm();

  const {
    resolvedAttendees,
    handleAddAttendee,
    handleAttendeeUpdate,
    handleAttendeeRemove,
    getExistingEmails,
    findDuplicateEmails,
    hasUnresolvedNames,
    replaceDocumentAttendees
  } = useAttendeeManagement(Alias);

  const { isProcessingDocument, documentError, handleFormattedContentExtracted, processDocument } = useDocumentProcessing();

  const { handleOutlookWeb, collectExecutiveAssistants, handleSendNoteToEAs } = useOutlookIntegration();

  useSessionPersistence();

  const existingEmails = getExistingEmails();
  const duplicateEmails = findDuplicateEmails();
  const executiveAssistants = collectExecutiveAssistants(resolvedAttendees);
  const hasUnresolvedAttendeeNames = hasUnresolvedNames();

  const handleFileSelected = useCallback(
    async (file: File) => {
      const result = await processDocument(file, eventsApiContext, Alias, manuallyEditedMeetingFormDataFields);

      if (result.success && result.parsedAttendees) {
        replaceDocumentAttendees(result.parsedAttendees);
      }

      if (result.success && result.formUpdates) {
        setMeetingFormData((prev) => ({
          ...prev,
          ...result.formUpdates
        }));
      }

      // Close modal after successful processing
      if (result.success) {
        setShowUploadTemplateModal(false);
      }
      // Note: Modal remains open for errors to show error UI and retry options
    },
    [processDocument, eventsApiContext, Alias, manuallyEditedMeetingFormDataFields, replaceDocumentAttendees, setMeetingFormData]
  );

  const handleCreateMeeting = useCallback(() => {
    handleOutlookWeb(meetingFormData, resolvedAttendees);
  }, [handleOutlookWeb, meetingFormData, resolvedAttendees]);

  const handleSendNoteToExecutiveAssistants = useCallback(() => {
    handleSendNoteToEAs(meetingFormData, resolvedAttendees, (errorMessage) => {
      addFlashMessage('error', errorMessage);
    });
  }, [handleSendNoteToEAs, meetingFormData, resolvedAttendees, addFlashMessage]);

  const handleAddAttendeeWithTypes = useCallback(
    (employee: Employee, priority: 'required' | 'optional') => {
      handleAddAttendee(employee, priority);
    },
    [handleAddAttendee]
  );

  const handleShowUploadModal = useCallback(() => {
    // Always allow opening upload modal since we don't need full employee data anymore
    setShowUploadTemplateModal(true);
  }, []);

  const handleHideUploadModal = useCallback(() => {
    setShowUploadTemplateModal(false);
    // Clear any document errors when modal is manually closed
    // This ensures clean state for next upload attempt
  }, []);

  const handleMultiBadgerClick = useCallback(() => {
    const loginNames = resolvedAttendees.map((attendee) => attendee.selectedEmployee?.login_name).filter((name): name is string => Boolean(name));
    const url = generateMultiBadgerUrl(loginNames);
    window.open(url, '_blank');
  }, [resolvedAttendees]);

  const handleDownloadCsv = useCallback(() => {
    exportAttendeeHierarchyToCsv(resolvedAttendees, (errorMessage) => {
      addFlashMessage('error', errorMessage);
    });
  }, [resolvedAttendees, addFlashMessage]);

  const handleToolsChange = useCallback(({ detail }: { detail: { open: boolean } }) => {
    setToolsOpen(detail.open);
  }, []);

  const handleNavigationChange = useCallback(({ detail }: { detail: { open: boolean } }) => {
    setNavigationOpen(detail.open);
  }, []);

  return (
    <>
      <AppLayout
        navigation={<DFPHomePageSideNavigation />}
        // Setting this to false as EA huddle will be used by EA's only
        navigationOpen={navigationOpen}
        onNavigationChange={handleNavigationChange}
        contentType="table"
        maxContentWidth={Number.MAX_VALUE}
        tools={<InfoPanel />}
        toolsOpen={toolsOpen}
        onToolsChange={handleToolsChange}
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    {/* Email EAs Button - Only show when there are executive assistants */}
                    {executiveAssistants.length > 0 && (
                      <Button
                        variant="normal"
                        external={true}
                        onClick={handleSendNoteToExecutiveAssistants}
                        ariaLabel={`Executive Assistants Found: ${executiveAssistants
                          .map((ea) => ea.name)
                          .join(', ')}. Send note to coordinate availability before sending the meeting invite.`}
                      >
                        Email EAs ({executiveAssistants.length})
                      </Button>
                    )}

                    {/* Create Meeting Button */}
                    <Button
                      variant="primary"
                      external={true}
                      onClick={handleCreateMeeting}
                      iconName={hasUnresolvedAttendeeNames ? undefined : 'calendar'}
                      disabled={hasUnresolvedAttendeeNames || resolvedAttendees.length === 0}
                      disabledReason={
                        resolvedAttendees.length === 0
                          ? 'Please add attendees before creating the meeting'
                          : hasUnresolvedAttendeeNames
                          ? 'Please resolve all attendee names before creating the meeting'
                          : undefined
                      }
                    >
                      {hasUnresolvedAttendeeNames ? 'Create Meeting' : 'Create Meeting'}
                    </Button>
                  </SpaceBetween>
                }
              >
                EA Huddle
              </Header>
            }
          >
            <Form>
              {/* Bottom Row: Add Attendees and Document Preview */}
              <div className="meeting-content-grid">
                {/* Add Attendees - Always render since we don't need full employee data */}
                <AddAttendeesContainer
                  formData={meetingFormData}
                  onInputChange={handleInputChange}
                  isProcessingDocument={isProcessingDocument}
                  resolvedAttendees={resolvedAttendees}
                  existingEmails={existingEmails}
                  duplicateEmails={duplicateEmails}
                  onAddAttendee={handleAddAttendeeWithTypes}
                  onAttendeeUpdate={handleAttendeeUpdate}
                  onAttendeeRemove={handleAttendeeRemove}
                  executiveAssistants={executiveAssistants}
                  documentError={documentError}
                  onFileSelected={handleFileSelected}
                  onFormattedContentExtracted={handleFormattedContentExtracted}
                  showUploadModal={showUploadTemplateModal}
                  onShowUploadModal={handleShowUploadModal}
                  onHideUploadModal={handleHideUploadModal}
                  onMultiBadgerClick={handleMultiBadgerClick}
                  onDownloadCsv={handleDownloadCsv}
                />
              </div>
            </Form>
          </ContentLayout>
        }
      />
      {/* Flashbar notifications rendered via portal */}
      {flashMessages.length > 0 && (
        <Flashbar
          items={flashMessages.map((message) => ({
            type: message.type,
            content: message.content,
            dismissible: true,
            onDismiss: () => removeFlashMessage(message.id),
            id: message.id
          }))}
        />
      )}
    </>
  );
};
